import createDeepMerge from '@fastify/deepmerge';
import {
  DEFAULT_SLIPPAGE_TOLERANCE_PERCENTAGE,
  MAXIMUM_SLIPPAGE_TOLERANCE_PERCENTAGE,
} from 'constants/swap';
import { ChainId } from 'types';
import { createStoreSelectors } from 'utilities/createStoreSelectors';
import { extractEnumValues } from 'utilities/extractEnumValues';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export interface UserChainSettings {
  gaslessTransactions: boolean;
  showPausedAssets: boolean;
  showUserAssetsOnly: boolean;
  doNotShowImportPositionsModal: boolean;
  slippageTolerancePercentage: string;
  doNotShowUserBalances: boolean;
  doNotExpandGuide: boolean;
}

type UserSettings = Partial<Record<ChainId, Partial<UserChainSettings>>>;

export interface State {
  userSettings: UserSettings;
  setUserSettings: (input: {
    settings: Partial<UserChainSettings>;
    chainIds?: ChainId[];
  }) => void;
}

const deepMerge = createDeepMerge({ all: true });

const allChainIds = extractEnumValues(ChainId);
export const initialUserSettings: UserSettings = {
  [ChainId.ZKSYNC_MAINNET]: {
    gaslessTransactions: true,
  },
  [ChainId.ZKSYNC_SEPOLIA]: {
    gaslessTransactions: true,
  },
};

const useStore = create<State>()(
  persist(
    immer(set => ({
      userSettings: initialUserSettings,
      setUserSettings: ({ settings, chainIds = allChainIds }) =>
        set(state =>
          chainIds.forEach(chainId => {
            if (!state.userSettings[chainId]) {
              state.userSettings[chainId] = {};
            }

            state.userSettings[chainId] = {
              ...state.userSettings[chainId],
              ...settings,
            };
          }),
        ),
    })),
    {
      name: 'venus-global-store',
      merge: (persisted, current) => {
        const state = deepMerge(current, persisted) as State;

        const userSettings = Object.entries(state.userSettings).reduce<UserSettings>(
          (acc, [chainId, settings]) => ({
            ...acc,
            [chainId]: {
              ...settings,
              slippageTolerancePercentage:
                Number(settings.slippageTolerancePercentage) > MAXIMUM_SLIPPAGE_TOLERANCE_PERCENTAGE
                  ? DEFAULT_SLIPPAGE_TOLERANCE_PERCENTAGE
                  : settings.slippageTolerancePercentage,
            },
          }),
          state.userSettings,
        );

        return {
          ...state,
          userSettings,
        };
      },
    },
  ),
);

export const store = createStoreSelectors(useStore);
